import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ApplicationState } from "../../store";
import * as ScreenInvitesState from "../../store/ScreenInvites";
import * as Auth0State from "../../store/Auth0";
import queryString from "query-string";
import {
  formatLocalTime,
  inviteClassName,
  inviteStatus,
  screenInviteWithSortedScreens,
  isAdmin,
} from "../../helpers";
import {
  ScreenInviteResponse as ScreenInviteType,
  ScreenResponse,
} from "../../api/ApiClient";
import { Spinner } from "../shared/Spinner";
import Markdown from "react-markdown";
import MyAlerts, { MyAlertsRef } from "../shared/Alert";

import Modal from "../shared/Modal";

import "../shared/customModal.css";
import { RegisterOrEditScreen } from "../Admin/AdminScreens";
import SelectScreen from "../shared/SelectScreen";
import { MeState } from "../../store/Me";
import { Button } from "@mui/material";

interface Injected {
  auth0: Auth0State.Auth0State;
  me: MeState;
}

type OwnProps = {
  header?: string;
};

const validTimeFormat = "MMM DD h:mm A, YYYY";

const ScreenInvite: React.FC<OwnProps> = ({ header }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { id } = queryString.parse(location.search || "");

  // Redux state
  const auth0 = useSelector((state: ApplicationState) => state.auth0);
  const me = useSelector((state: ApplicationState) => state.me);

  // Redux actions
  const { fetchScreenInvite } = ScreenInvitesState.actionCreators;

  const [fetchingInvite, setFetchingInvite] = useState(false);
  const [invite, setInvite] = useState<ScreenInviteType | null>(null);
  const myAlertsRef = useRef<MyAlertsRef>(null);

  const inviteId = (id || "") as string;

  const fetchInviteHandler = useCallback(async () => {
    if (!inviteId) {
      alert("Please select an invite");
      return;
    }

    setFetchingInvite(true);
    try {
      const actionResult = await dispatch(
        fetchScreenInvite({
          id: inviteId,
        }) as any,
      );
      setInvite(
        screenInviteWithSortedScreens(
          (actionResult as any).screenInvite as ScreenInviteType,
        ),
      );
    } catch (e) {
      alert("Error fetching invite: " + JSON.stringify(e));
    } finally {
      setFetchingInvite(false);
    }
  }, [inviteId, dispatch, fetchScreenInvite]);

  const onUpdatedScreen = useCallback(() => {
    fetchInviteHandler();
  }, [fetchInviteHandler]);

  useEffect(() => {
    fetchInviteHandler();
  }, [fetchInviteHandler]);
  if (fetchingInvite) {
    return <Spinner />;
  }

  const now = new Date();
  const { connectUser = null, validFrom = "", validTo = "" } = invite || {};
  const statusStr = invite !== null ? inviteStatus(now, invite) : "Scheduled";

  return (
    <div>
      <h3>{header}</h3>
      <hr />

      <MyAlerts ref={myAlertsRef} />

      {invite && (
        <div>
          <p />
          <div>
            <b>From</b>
          </div>
          <div>
            {connectUser
              ? connectUser.name || connectUser.externalId || "unknown"
              : "unknown"}
          </div>
          <p />

          {invite.details && (
            <div className="panel panel-info">
              <div className="panel-heading">Details from inviter:</div>
              <div className="panel-body">
                <Markdown>{invite.details as any}</Markdown>
              </div>
              <p />
            </div>
          )}

          {(validFrom || validTo) && (
            <div>
              <div className={`text-${inviteClassName(now, invite)}`}>
                <b>{statusStr}</b>
              </div>
              <div>
                {formatLocalTime(validFrom as any, validTimeFormat) ||
                  "any time"}{" "}
                -{" "}
                {formatLocalTime(validTo as any, validTimeFormat) || "any time"}
              </div>
              <p />
            </div>
          )}
        </div>
      )}

      {invite && (
        <ScreenInviteComp
          screenInvite={invite}
          onUpdatedScreen={onUpdatedScreen}
        />
      )}

      <div>
        {isAdmin(me.user) && (
          <React.Fragment>
            {" "}
            | <Link to="/allScreenInvites">View all screen invites</Link>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default ScreenInvite;

//// ScreenInviteComp

type ScreenInviteProps = {
  screenInvite: ScreenInviteType;
  onUpdatedScreen: () => any;
};

const ScreenInviteComp: React.FC<ScreenInviteProps> = ({
  screenInvite,
  onUpdatedScreen,
}) => {
  const [editingScreen, setEditingScreen] = useState<ScreenResponse | null>(
    null,
  );

  const showEditScreenModal = useCallback((screen: ScreenResponse) => {
    setEditingScreen(screen);
  }, []);

  const closeModal = useCallback(() => {
    setEditingScreen(null);
  }, []);

  const onUpdatedScreenHandler = useCallback(async () => {
    closeModal();
    await onUpdatedScreen();
  }, [closeModal, onUpdatedScreen]);

  const renderScreenButton = useCallback(
    (screen: ScreenResponse) => {
      return (
        <div>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={() => showEditScreenModal(screen)}
          >
            Edit
          </Button>
        </div>
      );
    },
    [showEditScreenModal],
  );

  const renderScreensTable = useCallback(() => {
    const screens = (screenInvite.screenTiles || []).map((s) => s.screen);

    return (
      <div>
        &nbsp;
        <p />
        <SelectScreen
          pageSize={5}
          //@ts-ignore
          screensProp={screens}
          renderButton={renderScreenButton}
          unselectedLabel="Delete"
          unselectedBtnClass="btn-danger"
        />
        <hr />
      </div>
    );
  }, [screenInvite.screenTiles, renderScreenButton]);

  return (
    <div>
      <br />
      <div>{renderScreensTable()}</div>

      <Modal
        show={!!editingScreen}
        onHide={closeModal}
        aria-labelledby="ModalHeader"
        dialogClassName="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="ModalHeader">Edit Screen Tile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <RegisterOrEditScreen
            //@ts-ignore
            existingScreen={editingScreen}
            screenInviteId={screenInvite.id}
            accountId={""}
            onRegistered={() => onUpdatedScreenHandler()}
          />
        </Modal.Body>
        <Modal.Footer>
          <Modal.Dismiss className="btn btn-default">Close</Modal.Dismiss>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
